<template>
  <div class="dataPointRows" :class="{ compact }">
    <div v-if="rows.length === 0" class="empty">No data points.</div>
    <template v-else>
      <button
        v-for="(item, idx) in rows"
        :key="rowKey(item, idx)"
        class="dataPointRow"
        :class="{ interactive }"
        :disabled="!interactive"
        @click="handleClick(item)"
      >
        <span class="dataPointIcon" :style="{ color: iconColor(item) }">
          <AppIcon :name="iconName(item)" :size="compact ? 15 : 16" :color="iconColor(item)" />
        </span>
        <span class="dataPointLine">
          <span class="dataPointLabel">{{ displayLabel(item) }}</span>
          <span v-if="shouldShowField(item)" class="dataPointField"> · {{ displayObjectField(item) }}</span>
          <span v-if="displaySnippet(item)" class="dataPointSnippet"> — {{ displaySnippet(item) }}</span>
        </span>
      </button>
    </template>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import AppIcon from './AppIcon.vue'

const props = defineProps({
  dataPoints: { type: Array, default: () => [] },
  compact: { type: Boolean, default: false },
  interactive: { type: Boolean, default: true },
})

const emit = defineEmits(['select'])

const rows = computed(() => Array.isArray(props.dataPoints) ? props.dataPoints.filter((item) => item && typeof item === 'object') : [])

function rowKey(item, idx) {
  const objectType = String(item?.object_type || '').trim()
  const objectId = String(item?.object_id || '').trim()
  const objectField = String(item?.object_field || '').trim()
  const label = String(item?.label || '').trim()
  return `${objectType}:${objectId}:${objectField}:${label}:${idx}`
}

function normalizeObjectType(item) {
  return String(item?.object_type || 'about').trim().toLowerCase() || 'about'
}

function displayObjectField(item) {
  return String(item?.object_field || '').trim()
}

function displayObjectId(item) {
  return String(item?.object_id || '—').trim() || '—'
}

function displayLabel(item) {
  const label = String(item?.label || '').trim()
  return label || `${normalizeObjectType(item)} ${displayObjectId(item)}`
}

function displaySnippet(item) {
  return String(item?.snippet || '').trim()
}

function shouldShowField(item) {
  const field = displayObjectField(item)
  if (!field) return false
  const objectType = normalizeObjectType(item)
  return !new Set(['email', 'reachout', 'task', 'followup', 'signal', 'event', 'gong_call', 'call']).has(objectType)
}

function iconName(item) {
  const objectType = normalizeObjectType(item)
  if (objectType === 'event') return 'salesforce_event'
  if (objectType === 'email') return 'salesforce_email'
  return objectType
}

function iconColor(item) {
  const objectType = normalizeObjectType(item)
  if (objectType === 'account') return '#2563eb'
  if (objectType === 'contact') return '#7c3aed'
  if (objectType === 'opportunity') return '#16a34a'
  if (objectType === 'reachout') return '#0891b2'
  if (objectType === 'task') return '#d97706'
  if (objectType === 'signal') return '#db2777'
  if (objectType === 'email') return '#0284c7'
  if (objectType === 'event') return '#9333ea'
  if (objectType === 'story') return '#475569'
  if (objectType === 'crm_update') return '#0284c7'
  if (objectType === 'salesforce_campaign_member') return '#ea580c'
  return 'var(--muted)'
}

function handleClick(item) {
  if (!props.interactive) return
  emit('select', item)
}
</script>

<style scoped>
.dataPointRows {
  display: flex;
  flex-direction: column;
}

.dataPointRow {
  border: none;
  border-bottom: 1px solid var(--border);
  background: transparent;
  color: inherit;
  padding: 10px 14px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  text-align: left;
}

.dataPointRow.interactive {
  cursor: pointer;
}

.dataPointRow.interactive:hover {
  background: var(--row-hover-bg);
}

.dataPointRow:last-child {
  border-bottom: none;
}

.dataPointIcon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 18px;
  min-width: 18px;
  padding-top: 1px;
}

.dataPointLine {
  min-width: 0;
  color: var(--text);
  font-size: 13px;
  line-height: 1.45;
  white-space: normal;
  overflow-wrap: anywhere;
}

.dataPointLabel {
  font-weight: 600;
}

.dataPointField,
.dataPointSnippet {
  color: var(--muted);
}

.dataPointRows.compact .dataPointRow {
  padding: 8px 0;
  gap: 8px;
  border-bottom: none;
}

.dataPointRows.compact .dataPointLine {
  font-size: 12px;
  line-height: 1.4;
}

.dataPointRows.compact .dataPointIcon {
  width: 16px;
  min-width: 16px;
}

.empty {
  padding: 14px;
  color: var(--muted);
  font-size: 13px;
}

.dataPointRows.compact .empty {
  padding: 0;
  font-size: 12px;
}
</style>
